/**
 * Apply `ClassTransform`s to a ClassDef. Pure: the input is deep-cloned first and never mutated.
 * Talents, gem bonuses, legendaries, trinkets and set bonuses all go through here.
 */
import type { AbilityDef, AuraDef, AreaShape, BindKey, ClassDef, EffectSpec, ResourceAmount } from '@keystone/sim';
import type { ClassTransform, EffectList } from './types';

export class TransformError extends Error {
  constructor(
    message: string,
    readonly transform: ClassTransform,
  ) {
    super(message);
  }
}

/** Deep clone of plain JSON data (content defs contain no functions); shared objects are un-shared (see treeClone). */
export function clone<T>(x: T): T {
  return treeClone(x);
}

/**
 * Deep clone that also UN-shares objects: every reference gets its own copy. structuredClone
 * keeps aliasing, and content reuses constants (e.g. Cleave's damage and Wounds effects share one
 * `CLEAVE_AREA`; Bladestorm's ticks and its channel-end share `BLADESTORM_AREA`), so an in-place
 * edit like modifyArea/scaleAmount would hit the shared object once per reference: Sweeping
 * Strikes made Cleave hit 5+3+3 = 11 instead of 8. Transforms must see a tree, not a graph.
 */
export function treeClone<T>(x: T): T {
  const stack = new Set<object>();
  const walk = (v: unknown): unknown => {
    if (!v || typeof v !== 'object') return v;
    if (stack.has(v)) throw new Error('treeClone: cyclic content definition');
    stack.add(v);
    let out: unknown;
    if (Array.isArray(v)) out = v.map(walk);
    else {
      const o: Record<string, unknown> = {};
      for (const [k, val] of Object.entries(v)) o[k] = walk(val);
      out = o;
    }
    stack.delete(v);
    return out;
  };
  return walk(x) as T;
}

/** Apply transforms in order to a copy of `def`. Throws TransformError on a missing target. */
export function applyTransforms(def: ClassDef, transforms: readonly ClassTransform[]): ClassDef {
  const out = treeClone(def);
  for (const t of transforms) applyOne(out, resolveAuraRefs(out, resolveBinds(out, t)));
  return out;
}

/** Name marker of an aura reference (see `auraRef`). */
export const AURA_REF = '@auraRef';

/**
 * An `applyAura` aura that stands for "the class's own aura with this id" (procs the class
 * defines inline, e.g. Riposte! or Sudden Death). Resolved to a deep copy of that AuraDef when
 * the transform is applied; a missing id is a TransformError (never a silent no-op).
 */
export function auraRef(id: string): AuraDef {
  return { id, name: AURA_REF, kind: 'buff', durationMs: 0, stacking: 'unique' };
}

/** Replace every `auraRef` inside a transform with the class's real AuraDef. */
export function resolveAuraRefs(def: ClassDef, t: ClassTransform): ClassTransform {
  if (!JSON.stringify(t).includes(AURA_REF)) return t;
  const walk = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map(walk);
    if (v && typeof v === 'object') {
      const o = v as Record<string, unknown>;
      if (o['name'] === AURA_REF && typeof o['id'] === 'string') {
        const real = findAura(def, o['id']);
        if (!real) throw new TransformError(`class ${def.id}: no aura ${o['id']} (auraRef)`, t);
        return clone(real);
      }
      const out: Record<string, unknown> = {};
      for (const [k, val] of Object.entries(o)) out[k] = walk(val);
      return out;
    }
    return v;
  };
  return walk(t) as ClassTransform;
}

/** First inline AuraDef with this id (abilities, passives), or undefined. */
export function findAura(def: ClassDef, id: string): AuraDef | undefined {
  let hit: AuraDef | undefined;
  forEachAura(def, (a) => {
    if (!hit && a.id === id && a.name !== AURA_REF) hit = a;
  });
  return hit;
}

/**
 * Ability and aura ids a list of transforms needs from the class (targets, `auraRef`s,
 * `modifyCooldown` effects and aura conditions inside added effects). Abilities/auras that the
 * same list adds (`addAbility`, `addPassive`, `registerAura`) are excluded.
 */
export function transformTargets(transforms: readonly ClassTransform[]): { abilities: string[]; auras: string[] } {
  const abilities = new Set<string>();
  const auras = new Set<string>();
  const provided = new Set<string>();
  const walk = (v: unknown, key?: string): void => {
    if (Array.isArray(v)) return v.forEach((x) => walk(x));
    if (!v || typeof v !== 'object') return;
    const o = v as Record<string, unknown>;
    if (o['name'] === AURA_REF && typeof o['id'] === 'string') auras.add(o['id']);
    if (o['type'] === 'modifyCooldown' && typeof o['abilityId'] === 'string') abilities.add(o['abilityId']);
    if ((o['type'] === 'selfHasAura' || o['type'] === 'targetHasAura') && typeof o['auraId'] === 'string') auras.add(o['auraId']);
    for (const [k, val] of Object.entries(o)) walk(val, k);
    void key;
  };
  for (const t of transforms) {
    if ('abilityId' in t && typeof t.abilityId === 'string') abilities.add(t.abilityId);
    if (t.op === 'patchAura' || t.op === 'addProc' || t.op === 'addAuraMods') auras.add(t.auraId);
    if (t.op === 'addAbility') provided.add(t.ability.id);
    if (t.op === 'addPassive' || t.op === 'registerAura') provided.add(t.aura.id);
    walk(t);
  }
  // Auras applied inline by the transforms themselves (not refs) also count as provided.
  const inline = (v: unknown): void => {
    if (Array.isArray(v)) return v.forEach(inline);
    if (!v || typeof v !== 'object') return;
    const o = v as Record<string, unknown>;
    if (o['type'] === 'applyAura' && o['aura'] && typeof o['aura'] === 'object') {
      const a = o['aura'] as Record<string, unknown>;
      if (a['name'] !== AURA_REF && typeof a['id'] === 'string') provided.add(a['id']);
    }
    for (const val of Object.values(o)) inline(val);
  };
  inline(transforms);
  return { abilities: [...abilities].filter((x) => !provided.has(x)).sort(), auras: [...auras].filter((x) => !provided.has(x)).sort() };
}

/** Ids from `transformTargets` that `def` does not have (empty = the transforms can apply). */
export function missingTargets(def: ClassDef, transforms: readonly ClassTransform[]): string[] {
  const t = transformTargets(transforms);
  const out: string[] = [];
  for (const a of t.abilities) if (!def.abilities.some((x) => x.id === a)) out.push(`ability:${a}`);
  for (const a of t.auras) if (!findAura(def, a)) out.push(`aura:${a}`);
  return out;
}

/** Placeholder for "the ability on this key" in class-generic transforms (gem bonuses, trinkets). */
export const bindRef = (key: BindKey): string => `@bind:${key}`;

/** Replace every `@bind:<key>` string inside a transform with the class's bound ability id. */
export function resolveBinds<T>(def: ClassDef, x: T): T {
  const walk = (v: unknown): unknown => {
    if (typeof v === 'string' && v.startsWith('@bind:')) {
      const id = def.binds[v.slice(6) as BindKey];
      if (!id) throw new Error(`class ${def.id}: no bind ${v}`);
      return id;
    }
    if (Array.isArray(v)) return v.map(walk);
    if (v && typeof v === 'object') {
      const o: Record<string, unknown> = {};
      for (const [k, val] of Object.entries(v)) o[k] = walk(val);
      return o;
    }
    return v;
  };
  return walk(x) as T;
}

function ability(def: ClassDef, t: ClassTransform & { abilityId: string }): AbilityDef {
  const a = def.abilities.find((x) => x.id === t.abilityId);
  if (!a) throw new TransformError(`class ${def.id}: no ability ${t.abilityId}`, t);
  return a;
}

function list(a: AbilityDef, which: EffectList | undefined): EffectSpec[] {
  if (which === 'channelStartEffects') return (a.channelStartEffects ??= []);
  if (which === 'channelEndEffects') return (a.channelEndEffects ??= []); // +rotation rework (F10)
  return a.effects;
}

function setResource(arr: ResourceAmount[] | undefined, resource: string, amount: number): ResourceAmount[] | undefined {
  const rest = (arr ?? []).filter((r) => r.resource !== resource);
  if (amount !== 0) rest.push({ resource, amount });
  return rest.length ? rest : undefined;
}

/** Every inline AuraDef in the class (abilities' effect trees + passives), all copies. */
export function forEachAura(def: ClassDef, fn: (a: AuraDef) => void): void {
  const walkEffects = (effects: readonly EffectSpec[] | undefined): void => {
    for (const e of effects ?? []) {
      switch (e.type) {
        case 'applyAura':
          walkAura(e.aura);
          break;
        case 'zone':
          if (e.insideAura) walkAura(e.insideAura);
          walkEffects(e.tick?.effects);
          break;
        case 'telegraph':
          walkEffects(e.effects);
          break;
        case 'conditional':
          walkEffects(e.then);
          walkEffects(e.else);
          break;
        default:
          break;
      }
    }
  };
  const walkAura = (a: AuraDef): void => {
    fn(a);
    walkEffects(a.periodic?.effects);
    walkEffects(a.onApply);
    walkEffects(a.onRemove);
    for (const p of a.procs ?? []) walkEffects(p.effects);
  };
  for (const ab of def.abilities) {
    walkEffects(ab.effects);
    walkEffects(ab.channelStartEffects);
    walkEffects(ab.channelEndEffects); // +rotation rework (F10)
  }
  for (const p of def.passives ?? []) walkAura(p);
}

function scaleShape(s: AreaShape, mult: number): void {
  if (mult === 1) return;
  if (s.type === 'circle') s.radius *= mult;
  else if (s.type === 'cone') s.range *= mult;
  else if (s.type === 'ring') s.outer *= mult;
  else if (s.type === 'line') s.length *= mult;
  else {
    s.w *= mult;
    s.h *= mult;
  }
}

function applyOne(def: ClassDef, t: ClassTransform): void {
  switch (t.op) {
    case 'patchAbility':
      Object.assign(ability(def, t), clone(t.patch));
      return;
    case 'addEffects': {
      const l = list(ability(def, t), t.list);
      if (t.position === 'start') l.unshift(...clone(t.effects));
      else l.push(...clone(t.effects));
      return;
    }
    case 'removeEffects': {
      const a = ability(def, t);
      const l = list(a, t.list);
      let n = 0;
      const kept = l.filter((e) => (e.type === t.effectType ? (t.index === undefined ? false : n++ !== t.index) : true));
      l.splice(0, l.length, ...kept);
      return;
    }
    case 'scaleAmount': {
      const scaleList = (l: readonly EffectSpec[]): void => {
        for (const e of l) {
          if (t.deep && e.type === 'conditional') {
            scaleList(e.then);
            if (e.else) scaleList(e.else);
            continue;
          }
          if (e.type !== t.effectType || !('amount' in e)) continue;
          const amt = e.amount;
          if (amt.base !== undefined) amt.base *= t.mult;
          if (amt.coef !== undefined) amt.coef *= t.mult;
          if (amt.pctSourceMaxHp !== undefined) amt.pctSourceMaxHp *= t.mult;
          if (amt.pctTargetMaxHp !== undefined) amt.pctTargetMaxHp *= t.mult;
          if (amt.pctRecentDamageTaken) amt.pctRecentDamageTaken.pct *= t.mult;
        }
      };
      scaleList(list(ability(def, t), t.list));
      return;
    }
    case 'modifyArea': {
      const l = list(ability(def, t), t.list);
      const delta = t.maxTargetsDelta ?? 0;
      const mult = t.radiusMult ?? 1;
      let hit = false;
      for (const e of l) {
        if (e.area) {
          if (delta && e.area.maxTargets !== undefined) e.area.maxTargets = Math.max(1, e.area.maxTargets + delta);
          scaleShape(e.area.shape, mult);
          hit = true;
        }
        if (e.type === 'zone' || e.type === 'telegraph') {
          scaleShape(e.shape, mult);
          hit = true;
        }
      }
      if (!hit) throw new TransformError(`ability ${t.abilityId}: no area effect`, t);
      return;
    }
    case 'modifyCooldown': {
      const a = ability(def, t);
      if (t.remove) {
        delete a.cooldown;
        return;
      }
      const cd = (a.cooldown ??= { durationMs: 0 });
      if (t.durationMs !== undefined) cd.durationMs = t.durationMs;
      if (t.deltaMs !== undefined) cd.durationMs = Math.max(0, cd.durationMs + t.deltaMs);
      if (t.charges !== undefined) cd.charges = t.charges;
      return;
    }
    case 'changeTargeting': {
      const a = ability(def, t);
      a.targeting = t.targeting;
      if (t.range !== undefined) a.range = t.range;
      if (t.requiresFacing !== undefined) a.requiresFacing = t.requiresFacing;
      return;
    }
    case 'changeCast':
      ability(def, t).cast = clone(t.cast);
      return;
    case 'modifyResource': {
      const a = ability(def, t);
      const next = setResource(a[t.field], t.resource, t.amount);
      if (next) a[t.field] = next;
      else delete a[t.field];
      return;
    }
    case 'patchAura': {
      let found = false;
      forEachAura(def, (a) => {
        if (a.id !== t.auraId) return;
        Object.assign(a, clone(t.patch));
        found = true;
      });
      if (!found) throw new TransformError(`class ${def.id}: no aura ${t.auraId}`, t);
      return;
    }
    case 'addPassive':
      (def.passives ??= []).push(clone(t.aura));
      return;
    case 'addAuraMods': {
      let found = false;
      forEachAura(def, (a) => {
        if (a.id !== t.auraId) return;
        a.mods = [...(a.mods ?? []), ...clone(t.mods)];
        found = true;
      });
      if (!found) throw new TransformError(`class ${def.id}: no aura ${t.auraId}`, t);
      return;
    }
    case 'addProc': {
      let found = false;
      forEachAura(def, (a) => {
        if (a.id !== t.auraId) return;
        (a.procs ??= []).push(clone(t.proc));
        found = true;
      });
      if (!found) throw new TransformError(`class ${def.id}: no aura ${t.auraId}`, t);
      return;
    }
    case 'addAbility':
      if (def.abilities.some((a) => a.id === t.ability.id)) throw new TransformError(`duplicate ability ${t.ability.id}`, t);
      def.abilities.push(clone(t.ability));
      (def.extraAbilities ??= []).push(t.ability.id);
      return;
    case 'patchResource': {
      const r = def.resources.find((x) => x.id === t.resourceId);
      if (!r) throw new TransformError(`class ${def.id}: no resource ${t.resourceId}`, t);
      Object.assign(r, clone(t.patch));
      return;
    }
    case 'registerAura':
      return; // not a ClassDef edit: compileCharacter collects these into registerAuras
  }
}
